import React from 'react';
import {
  Dialog, IconButton, Box, Typography, Chip, CircularProgress, Stack, Tooltip, alpha, useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DownloadIcon from '@mui/icons-material/Download';
import { formatFileSize } from './pathUtils';

/**
 * Image lightbox with keyboard navigation across all images in the current folder.
 * Arrow keys move prev/next, Escape closes.
 */
export default function Lightbox({
  open,
  images, // [{ name, fullPath, size, lastModified, fileType, logicalPath, s3Key }]
  startPath,
  onClose,
  buildPublicUrl,
}) {
  const theme = useTheme();
  const [index, setIndex] = React.useState(0);
  const [imageDims, setImageDims] = React.useState(null);

  React.useEffect(() => {
    if (!open) return;
    const start = Math.max(0, images.findIndex((i) => i.logicalPath === startPath));
    setIndex(start === -1 ? 0 : start);
  }, [open, startPath, images]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') setIndex((i) => (i - 1 + images.length) % images.length);
      if (e.key === 'ArrowRight') setIndex((i) => (i + 1) % images.length);
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, images.length, onClose]);

  if (!open || images.length === 0) return null;
  const current = images[index];
  const url = current ? buildPublicUrl(current.fullPath || current.s3Key) : null;

  return (
    <Dialog open={open} onClose={onClose} fullScreen
      PaperProps={{ sx: { bgcolor: alpha('#000', 0.92) } }}>
      <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 2, display: 'flex', gap: 1 }}>
        <Tooltip title="Download">
          <IconButton component="a" href={url} download={current?.name} target="_blank" rel="noopener" sx={{ color: '#fff' }}>
            <DownloadIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Close">
          <IconButton onClick={onClose} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, p: 2, color: '#fff', zIndex: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{current?.name}</Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
          <Chip size="small" label={`${index + 1} / ${images.length}`} />
          {current?.size != null && <Chip size="small" label={formatFileSize(current.size)} />}
          {imageDims && <Chip size="small" label={`${imageDims.w} × ${imageDims.h}`} />}
        </Stack>
      </Box>

      <Tooltip title="Previous">
        <IconButton onClick={() => setIndex((i) => (i - 1 + images.length) % images.length)}
          sx={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#fff', zIndex: 2 }}>
          <ChevronLeftIcon fontSize="large" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Next">
        <IconButton onClick={() => setIndex((i) => (i + 1) % images.length)}
          sx={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: '#fff', zIndex: 2 }}>
          <ChevronRightIcon fontSize="large" />
        </IconButton>
      </Tooltip>

      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 8 }}>
        {!url ? <CircularProgress sx={{ color: '#fff' }} /> : (
          <img
            src={url}
            alt={current?.name}
            onLoad={(e) => setImageDims({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
          />
        )}
      </Box>
    </Dialog>
  );
}
